// Lógica pura del editor de plantillas (sin CodeMirror): localizar las referencias {{…}},
// validarlas contra las variables del evento y decidir qué autocompletar según el cursor.
// Misma sintaxis que el motor del backend: {{ Nombre }}, {{Nombre|formato}}, {{Nombre|formato:argumento}}.

export interface ReferenciaEnTexto {
  desde: number;
  hasta: number;
  nombre: string;
  formato: string | null;
}

export interface ProblemaEnPlantilla {
  desde: number;
  hasta: number;
  mensaje: string;
}

const REFERENCIA = /\{\{\s*([^{}|]*?)\s*(?:\|\s*([^{}:]*?)\s*(?::[^{}]*)?)?\}\}/g;
const NOMBRE_VALIDO = /^[A-Za-z_][\w.]*$/;

export function referencias(texto: string): ReferenciaEnTexto[] {
  const lista: ReferenciaEnTexto[] = [];
  for (const m of texto.matchAll(REFERENCIA)) {
    lista.push({ desde: m.index!, hasta: m.index! + m[0].length, nombre: m[1], formato: m[2] ?? null });
  }
  return lista;
}

/** Referencias con variable inexistente, nombre mal escrito o formato desconocido, y llaves sin cerrar. */
export function problemas(texto: string, variables: Iterable<string>, formatos: Iterable<string>): ProblemaEnPlantilla[] {
  const nombres = new Set([...variables].map((v) => v.toLowerCase()));
  const fmts = new Set([...formatos].map((f) => f.toLowerCase()));
  const lista: ProblemaEnPlantilla[] = [];
  for (const r of referencias(texto)) {
    if (!r.nombre) lista.push({ desde: r.desde, hasta: r.hasta, mensaje: "Falta el nombre de la variable." });
    else if (!NOMBRE_VALIDO.test(r.nombre)) lista.push({ desde: r.desde, hasta: r.hasta, mensaje: `'${r.nombre}' no es un nombre de variable válido.` });
    else if (!nombres.has(r.nombre.toLowerCase()))
      lista.push({ desde: r.desde, hasta: r.hasta, mensaje: `La variable '${r.nombre}' no existe en el evento conectado.` });
    if (r.formato !== null && !fmts.has(r.formato.toLowerCase()))
      lista.push({ desde: r.desde, hasta: r.hasta, mensaje: `Formato desconocido: '${r.formato}'.` });
  }
  // "{{" sin su "}}" (el motor lo copiaría tal cual).
  const cerradas = referencias(texto);
  for (const m of texto.matchAll(/\{\{/g)) {
    if (!cerradas.some((r) => m.index! >= r.desde && m.index! < r.hasta))
      lista.push({ desde: m.index!, hasta: m.index! + 2, mensaje: "Estas llaves no se cierran con }}." });
  }
  return lista;
}

export type ContextoAutocompletado =
  | { tipo: "variable"; desde: number; filtro: string }
  | { tipo: "formato"; desde: number; filtro: string }
  | null;

/** Qué sugerir con el texto que hay ANTES del cursor dentro de la línea. */
export function contextoAutocompletado(antesDelCursor: string, posicionInicio: number): ContextoAutocompletado {
  const formato = /\{\{\s*[\w.]+\s*\|\s*(\w*)$/.exec(antesDelCursor);
  if (formato) return { tipo: "formato", desde: posicionInicio + antesDelCursor.length - formato[1].length, filtro: formato[1] };
  const variable = /\{\{\s*([\w.]*)$/.exec(antesDelCursor);
  if (variable) return { tipo: "variable", desde: posicionInicio + antesDelCursor.length - variable[1].length, filtro: variable[1] };
  return null;
}

/** Texto a insertar al elegir una variable: agrega el cierre "}}" solo si no está ya después del cursor. */
export function textoAlElegirVariable(nombre: string, despuesDelCursor: string): string {
  return /^\s*(\|[^{}]*)?\}\}/.test(despuesDelCursor) ? nombre : `${nombre}}}`;
}
